import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1050;
  background: rgba(0, 0, 0, 0.78);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px 16px 32px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  box-sizing: border-box;

  @media screen and (min-width: 769px) {
    padding-top: 24px;
    padding-left: 24px;
    padding-right: 24px;
  }

  @media screen and (max-width: 768px) {
    padding: 16px 12px 24px;
    padding-top: max(16px, env(safe-area-inset-top, 0px));
    padding-left: max(12px, env(safe-area-inset-left, 0px));
    padding-right: max(12px, env(safe-area-inset-right, 0px));
    padding-bottom: max(24px, env(safe-area-inset-bottom, 0px));
    min-height: 100dvh;
    height: 100dvh;
    overflow: hidden;
  }
`

export const GalleryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  max-width: 1120px;
  flex-shrink: 0;
  margin-bottom: 16px;
  padding: 0 32px;
  box-sizing: border-box;

  @media screen and (min-width: 769px) {
    align-self: stretch;
    justify-content: flex-start;
    max-width: none;
    padding: 0;
  }

  @media screen and (max-width: 768px) {
    gap: 8px;
    margin-bottom: 12px;
    padding: 0 4px;
  }
`

export const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--white);
  font-family: 'Caixa Std', sans-serif;
  font-weight: 600;
  font-size: 32px;
  line-height: 1;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--white);
    outline-offset: 2px;
  }

  @media screen and (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 28px;
  }
`

export const GalleryTitle = styled.h2`
  margin: 0;
  margin-top: 10px;
  font-family: 'Caixa Std', sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.2;
  color: var(--white);
  display: flex;
  align-items: center;
  min-height: 40px;
  flex: 1 1 auto;
  min-width: 0;
  word-break: break-word;

  @media screen and (min-width: 769px) {
    flex: 0 1 auto;
    text-align: left;
    justify-content: flex-start;
  }

  @media screen and (max-width: 768px) {
    margin-top: 6px;
    font-size: 16px;
    min-height: 36px;
    line-height: 1.3;
  }
`

export const GalleryContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 1120px;
  flex: 1 1 auto;
  min-height: 0;
  margin: auto 0;

  @media screen and (max-width: 768px) {
    justify-content: center;
    margin: 0;
    flex: 1;
    width: 100%;
    min-height: 0;
    overflow: hidden;
    gap: 0;
  }
`

export const GalleryMain = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  @media screen and (max-width: 768px) {
    order: 2;
    flex: 0 1 auto;
    min-height: 0;
    align-items: stretch;
    justify-content: flex-start;
    margin-top: 0;
  }
`

export const SliderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;

  @media screen and (max-width: 768px) {
    display: grid;
    grid-template-columns: 1fr auto auto 1fr;
    grid-template-rows: auto auto;
    gap: 8px;
    align-items: center;
    align-content: start;
    width: 100%;
  }
`

export const NavButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--primary);
  cursor: pointer;

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }

  &[data-nav='prev'] {
    @media screen and (max-width: 768px) {
      grid-column: 2;
      grid-row: 2;
      width: 44px;
      height: 44px;
    }
  }

  &[data-nav='next'] {
    @media screen and (max-width: 768px) {
      grid-column: 3;
      grid-row: 2;
      width: 44px;
      height: 44px;
    }
  }

  @media screen and (max-width: 480px) {
    &[data-nav='prev'],
    &[data-nav='next'] {
      width: 40px;
      height: 40px;
    }
  }
`

export const NavIcon = styled(FontAwesomeIcon)`
  font-size: 2.5rem;

  @media screen and (max-width: 768px) {
    font-size: 2rem;
  }

  @media screen and (max-width: 480px) {
    font-size: 1.75rem;
  }
`

export const ImageFrame = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  @media screen and (max-width: 768px) {
    grid-column: 1 / -1;
    grid-row: 1;
    width: 100%;
  }
`

export const GalleryImage = styled.img`
  display: block;
  width: 100%;
  max-width: min(960px, 100%);
  max-height: min(52vh, 520px);
  object-fit: contain;

  @media screen and (max-width: 768px) {
    max-width: 100%;
    max-height: min(55dvh, 420px);
    margin: 0 auto;
  }

  @media screen and (max-width: 480px) {
    max-height: min(50dvh, 360px);
  }

  @media screen and (max-height: 700px) {
    max-height: min(45dvh, 280px);
  }
`

export const InfoSection = styled.div`
  width: 100%;
  max-width: 960px;
  margin-top: 24px;
  text-align: center;
  color: var(--white);
  padding: 0 8px;
  box-sizing: border-box;

  @media screen and (max-width: 768px) {
    order: 1;
    flex-shrink: 0;
    margin: 0;
    padding: 0 4px;
    max-width: 100%;
    max-height: 32dvh;
    overflow-y: auto;
    text-align: left;
  }
`

export const Description = styled.p`
  margin: 0 0 12px;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  word-break: break-word;
  overflow-wrap: anywhere;

  @media screen and (max-width: 768px) {
    font-size: 0.8125rem;
    margin-bottom: 6px;
    text-align: left;
  }
`

export const EquipmentText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  word-break: break-word;
  overflow-wrap: anywhere;

  strong {
    font-weight: 700;
  }

  @media screen and (max-width: 768px) {
    font-size: 0.8125rem;
    text-align: left;
  }
`
